import clsx from "clsx";
import React, { FunctionComponent, createRef } from "react";

import DangerouslySetCMSMarkup from "@ui/components/article/DangerouslySetCMSMarkup/DangerouslySetCMSMarkup";
import Button from "@ui/components/cta/Button";
import { SELECTORS } from "@ui/config";
import { withGracefulErrorBoundary } from "@ui/errors/GracefulErrorBoundary";
import useAnalyticsObserver from "@ui/hooks/use-analytics-observer";
import { sharedStyles } from "@ui/shared-styles";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { Banner } from "@voxmedia/duet-context/context/global-data-context";

import * as styles from "./WikiBanner.css";

interface Props {
  wikiBanner: Banner;
  template?: string;
  className?: string;
}

const WikiBanner: FunctionComponent<Props> = ({
  wikiBanner,
  template,
  className,
}) => {
  const ref = createRef<HTMLDivElement>();
  useAnalyticsObserver(ref, {
    event: "memberships_wiki_banner",
    memberships_wiki_banner: {
      cta: wikiBanner?.heading,
      action: "view",
      position: "wiki_banner",
    },
  });

  const analytics = useAnalytics();

  if (!wikiBanner) {
    return null;
  }

  return (
    <div
      className={clsx(
        SELECTORS.WikiBanner,
        template &&
          styles.postTemplate[template as keyof typeof styles.postTemplate],
        className,
      )}
      ref={ref}
    >
      <div
        className={clsx(sharedStyles.layout.mainColumn, styles.innerContainer)}
      >
        <h2 className={styles.heading}>{wikiBanner.heading}</h2>
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            {wikiBanner.content.map((c, i) => (
              <DangerouslySetCMSMarkup key={i} component="p" html={c.html} />
            ))}
          </div>
          <div className={styles.dekContainer}>
            {wikiBanner.dek && (
              <DangerouslySetCMSMarkup
                component="p"
                html={wikiBanner.dek}
                className={styles.dek}
              />
            )}
            <Button
              className={styles.button}
              href={wikiBanner.buttonUrl}
              text={wikiBanner.buttonText}
              onClick={() => {
                analytics?.emit({
                  event: "memberships_wiki_banner",
                  memberships_wiki_banner: {
                    cta: wikiBanner.heading,
                    action: `click_${wikiBanner.buttonText}`,
                    position: "wiki_banner",
                  },
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withGracefulErrorBoundary(WikiBanner, {
  context: "WikiBannerErrorBoundary",
});
